import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { DeskLayout } from '../../components/DeskLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Skeleton } from '../../components/ui/skeleton';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { useProperty } from '../../context/PropertyContext';
import { useAuth } from '../../context/AuthContext';
import { PropertyBadge } from '../../components/PropertyBadge';
import { categorizeMessage, calculateSLATimer, CATEGORY_TAGS, ESCALATION_REASONS, ESCALATION_ASSIGN_OPTIONS, GUEST_UPDATE_SCRIPTS } from '../../lib/workQueue';
import { 
  getConversations, 
  getMessages, 
  createConversation, 
  createMessage, 
  generateDraft,
  updateConversationStatus,
  updateNeedsReply,
  getProperty,
  seedData,
  createEscalation,
  updateTaskEP,
  getTasks,
  addConversationToDailyRun,
  archiveConversation,
} from '../../lib/api';
import { 
  Search, Plus, Send, PenLine, MessageSquare, Mail, Phone, Instagram, Loader2, Database, 
  Inbox as InboxIcon, RefreshCw, Copy, Check, Wifi, Clock, MapPin, Gift, MessageSquareReply, ChevronRight, 
  Save, SendHorizonal, HelpCircle, DollarSign, AlertTriangle, Timer, Flag, Coffee, Shield,
  ArrowRight, Archive, CheckCircle
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';
import { AddToRunModal } from '../../components/AddToRunModal';

// ---- IncomingRequestCard -------------------------------------------------
// A distinct card style for pending-intake conversations in Guest Inbox.
// Shows: guest, property, channel, request snippet, received-time, type,
// and the 3 primary actions: Add to Daily Run / Reply / Archive.
const IncomingRequestCard = ({ conv, propertyName, isSelected, onSelect, onAddToRun, onArchive, archiving }) => {
  const priority = conv.intake_priority || 'DUE_TODAY';
  const isUrgent = priority === 'HANDLE_NOW';
  const requestType = (conv.request_type || conv.intake_category || 'OTHER').replace(/_/g, ' ');
  const received = conv.intake_received_at || conv.lastMessageAt || conv.createdAt;
  const receivedAgo = received
    ? `${formatDistanceToNow(new Date(received), { addSuffix: true })}`
    : '';
  const channel = conv.source_channel || conv.channel || 'OTHER';
  const snippet = conv.lastMessageBody || conv.intake_summary || conv.subject || '';

  return (
    <div
      className={`group rounded-xl border mb-2 transition-all ${
        isSelected
          ? 'border-[#3F617E] bg-[#3F617E]/5 shadow-sm'
          : 'border-stone-200 bg-white hover:border-stone-300'
      }`}
      data-testid={`incoming-request-${conv.id}`}
    >
      <button
        onClick={onSelect}
        className="w-full text-left p-3 pb-2"
        data-testid={`incoming-request-select-${conv.id}`}
      >
        <div className="flex items-start justify-between gap-2 mb-1">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="font-medium text-sm text-stone-900 truncate">
                {conv.guestName || 'Guest'}
              </span>
              <Badge
                className={`text-[10px] h-4 px-1.5 ${
                  isUrgent ? 'bg-red-100 text-red-700 hover:bg-red-100' : 'bg-amber-100 text-amber-700 hover:bg-amber-100'
                }`}
              >
                New request
              </Badge>
              {conv.auto_acknowledged && (
                <Badge
                  className="text-[10px] h-4 px-1.5 bg-stone-100 text-stone-600 hover:bg-stone-100"
                  data-testid={`auto-ack-pill-${conv.id}`}
                  title="Guest received an automatic after-hours acknowledgement"
                >
                  Auto-acked
                </Badge>
              )}
              {conv.after_hours_urgent && (
                <Badge
                  className="text-[10px] h-4 px-1.5 bg-red-50 text-red-700 hover:bg-red-50 border border-red-200"
                  data-testid={`after-hours-urgent-pill-${conv.id}`}
                  title="Urgent keyword detected outside office hours"
                >
                  After-hours · urgent
                </Badge>
              )}
            </div>
            <div className="text-[11px] text-stone-500 mt-0.5 truncate">
              {propertyName} <span className="text-stone-300">·</span> {channel}{' '}
              <span className="text-stone-300">·</span> {requestType}
            </div>
          </div>
          {receivedAgo && (
            <span className="text-[10px] text-stone-400 whitespace-nowrap flex-shrink-0 pt-0.5">
              {receivedAgo}
            </span>
          )}
        </div>
        <p className="text-xs text-stone-600 line-clamp-2 leading-relaxed">{snippet}</p>
      </button>
      <div className="flex items-center gap-1.5 px-3 pb-3 pt-1 border-t border-stone-100">
        <Button
          size="sm"
          onClick={(e) => { e.stopPropagation(); onAddToRun(); }}
          className="h-7 px-2.5 text-xs bg-[#3F617E] hover:bg-[#345470] rounded-full flex-1"
          data-testid={`add-to-run-btn-${conv.id}`}
        >
          <ArrowRight className="h-3 w-3 mr-1" />
          Add to Daily Run
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={(e) => { e.stopPropagation(); onSelect(); }}
          className="h-7 px-2.5 text-xs rounded-full"
          data-testid={`reply-btn-${conv.id}`}
        >
          Open
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={(e) => { e.stopPropagation(); onArchive(); }}
          disabled={archiving}
          className="h-7 w-7 p-0 text-stone-400 hover:text-stone-700 hover:bg-stone-100 rounded-full"
          data-testid={`archive-btn-${conv.id}`}
          aria-label="Archive"
          title="Archive"
        >
          {archiving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Archive className="h-3.5 w-3.5" />}
        </Button>
      </div>
    </div>
  );
};

export const Inbox = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { properties, selectedPropertyId, setSelectedPropertyId, getPropertyName, refreshProperties } = useProperty();
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [conversations, setConversations] = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [draftLoading, setDraftLoading] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [sending, setSending] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showNewConv, setShowNewConv] = useState(false);
  const [selectedTone, setSelectedTone] = useState('WARM');
  const [aiDraft, setAiDraft] = useState('');
  const [showAssistPanel, setShowAssistPanel] = useState(true);
  const [newConvData, setNewConvData] = useState({
    propertyId: '',
    guestName: '',
    subject: '',
    channel: 'EMAIL',
    initialMessage: ''
  });
  const [calmMode, setCalmMode] = useState(false);
  const [showEscalateModal, setShowEscalateModal] = useState(false);
  const [linkedTask, setLinkedTask] = useState(null);
  const [addToRunOpen, setAddToRunOpen] = useState(false);
  const [addToRunConv, setAddToRunConv] = useState(null);
  const [archivingId, setArchivingId] = useState(null);
  const [epForm, setEpForm] = useState({ signal_type: '', impact_area: '', fix_route: '', owner_decision_needed: false, owner_decision_summary: '' });
  const [epSaving, setEpSaving] = useState(false);
  const [escalateData, setEscalateData] = useState({
    reason: '',
    assignTo: '',
    internalNotes: ''
  });

  useEffect(() => {
    loadData();
  }, [selectedPropertyId]);

  useEffect(() => {
    const convId = searchParams.get('conversation');
    if (convId && conversations.length > 0) {
      const conv = conversations.find(c => c.id === convId);
      if (conv) selectConversation(conv);
    }
  }, [searchParams, conversations]);

  const loadData = async () => {
    try {
      const params = {};
      if (selectedPropertyId !== 'all') {
        params.propertyId = selectedPropertyId;
      }
      const convsRes = await getConversations(params);
      setConversations(convsRes.data);
      
      // Auto-select first conversation if none selected
      if (convsRes.data.length > 0 && !searchParams.get('conversation')) {
        selectConversation(convsRes.data[0]);
      }
    } catch (error) {
      console.error('Failed to load inbox:', error);
    } finally {
      setLoading(false);
    }
  };

  const selectConversation = async (conv) => {
    setSelectedConv(conv);
    setSearchParams({ conversation: conv.id });
    setAiDraft('');
    setNewMessage('');
    setLinkedTask(null);
    
    try {
      const [msgsRes, propRes, tasksRes] = await Promise.all([
        getMessages(conv.id),
        getProperty(conv.propertyId),
        getTasks({ conversationId: conv.id }),
      ]);
      setMessages(msgsRes.data);
      setSelectedProperty(propRes.data);
      // Link first matching task for EP editing
      const task = tasksRes.data?.[0] || null;
      setLinkedTask(task);
      if (task) {
        setEpForm({
          signal_type: task.signal_type || '',
          impact_area: task.impact_area || '',
          fix_route: task.fix_route || '',
          owner_decision_needed: task.owner_decision_needed || false,
          owner_decision_summary: task.owner_decision_summary || '',
        });
      } else {
        setEpForm({ signal_type: '', impact_area: '', fix_route: '', owner_decision_needed: false, owner_decision_summary: '' });
      }
    } catch (error) {
      console.error('Failed to load conversation:', error);
    }
  };

  const handleSendMessage = async (sendForApproval = false) => {
    if (!newMessage.trim() || !selectedConv) return;
    
    setSending(true);
    try {
      await createMessage({
        conversationId: selectedConv.id,
        direction: 'OUTBOUND',
        bodyText: newMessage,
        category: 'OTHER',
        priority: 'NORMAL'
      });
      
      // Mark conversation as no longer needing reply
      const inboundMsgs = messages.filter(m => m.direction === 'INBOUND' && m.needsReply);
      for (const msg of inboundMsgs) {
        await updateNeedsReply(msg.id, false);
      }
      
      setNewMessage('');
      setAiDraft('');
      const msgsRes = await getMessages(selectedConv.id);
      setMessages(msgsRes.data);
      await loadData(); // Refresh conversation list
      
      toast.success(sendForApproval ? 'Sent for approval.' : 'Sent.');
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleGenerateDraft = async () => {
    if (!selectedConv || messages.length === 0) return;
    
    setDraftLoading(true);
    try {
      const lastInbound = [...messages].reverse().find(m => m.direction === 'INBOUND');
      const res = await generateDraft({
        propertyId: selectedConv.propertyId,
        conversationId: selectedConv.id,
        messageText: lastInbound?.bodyText || 'Guest inquiry',
        tone: selectedTone,
        context: 'reply'
      });
      setAiDraft(res.data.draft);
      toast.success('Draft ready.');
    } catch (error) {
      toast.error('Failed to generate draft');
    } finally {
      setDraftLoading(false);
    }
  };

  const handleRegenerateDraft = async () => {
    await handleGenerateDraft();
  };

  const handleCopyDraft = () => {
    navigator.clipboard.writeText(aiDraft);
    toast.success('Copied to clipboard');
  };

  const handleUseDraft = () => {
    setNewMessage(aiDraft);
    toast.success('Reply added to composer.');
  };

  const handleInsertQuickReply = (reply) => {
    setNewMessage(prev => prev ? `${prev}\n\n${reply}` : reply);
    toast.success('Reply added to composer.');
  };

  const handleSaveDraft = () => {
    toast.success('Draft saved.');
  };

  const handleEscalate = () => {
    setEscalateData({ reason: '', assignTo: '', internalNotes: '' });
    setShowEscalateModal(true);
  };

  const handleCreateEscalation = async () => {
    if (!escalateData.reason || !escalateData.assignTo || !selectedConv) {
      toast.error('Please select reason and assignee');
      return;
    }
    
    try {
      const res = await createEscalation({
        conversationId: selectedConv.id,
        reason: escalateData.reason,
        assignTo: escalateData.assignTo,
        internalNotes: escalateData.internalNotes
      });
      
      setShowEscalateModal(false);
      setNewMessage(res.data.guestUpdateScript);
      toast.success('Escalation created. Guest update is ready to send.');
    } catch (error) {
      toast.error('Failed to create escalation');
    }
  };

  const handleCreateConversation = async () => {
    if (!newConvData.propertyId || !newConvData.guestName || !newConvData.subject) {
      toast.error('Please fill all required fields');
      return;
    }
    
    try {
      const convRes = await createConversation({
        propertyId: newConvData.propertyId,
        guestName: newConvData.guestName,
        subject: newConvData.subject,
        channel: newConvData.channel
      });
      
      if (newConvData.initialMessage) {
        await createMessage({
          conversationId: convRes.data.id,
          direction: 'INBOUND',
          bodyText: newConvData.initialMessage,
          category: 'INQUIRY',
          priority: 'NORMAL'
        });
      }
      
      setShowNewConv(false);
      setNewConvData({ propertyId: '', guestName: '', subject: '', channel: 'EMAIL', initialMessage: '' });
      await loadData();
      selectConversation(convRes.data);
      toast.success('Conversation created');
    } catch (error) {
      toast.error('Failed to create conversation');
    }
  };

  const handleStatusChange = async (status) => {
    if (!selectedConv) return;
    try {
      await updateConversationStatus(selectedConv.id, status);
      setSelectedConv({ ...selectedConv, status });
      await loadData();
      toast.success(`Status updated to ${status}`);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleSeedData = async () => {
    setSeeding(true);
    try {
      await seedData();
      await refreshProperties();
      await loadData();
      toast.success('Showcase workspace loaded.');
    } catch (error) {
      if (error.response?.data?.message === 'Already seeded') {
        toast.info('Showcase workspace already exists');
        await loadData();
      } else {
        toast.error('Could not load showcase workspace');
      }
    } finally {
      setSeeding(false);
    }
  };

  // ---- Intake actions: Add to Daily Run / Archive ----------------------
  const openAddToRun = (conv) => {
    setAddToRunConv(conv);
    setAddToRunOpen(true);
  };

  const handleConfirmAddToRun = async (payload) => {
    if (!addToRunConv) return;
    try {
      await addConversationToDailyRun(addToRunConv.id, payload);
      setAddToRunOpen(false);
      setAddToRunConv(null);
      await loadData();
      // Refresh detail view if the same convo is selected
      if (selectedConv?.id === addToRunConv.id) {
        selectConversation({ ...addToRunConv, intake_status: 'ADDED_TO_RUN' });
      }
      toast.success('Added to Daily Run — it now shows in Today and Daily Run.');
    } catch {
      toast.error('Could not add to Daily Run. Please try again.');
    }
  };

  const handleArchiveIntake = async (conv) => {
    setArchivingId(conv.id);
    try {
      await archiveConversation(conv.id);
      await loadData();
      if (selectedConv?.id === conv.id) {
        setSelectedConv(null);
        setSearchParams({});
      }
      toast.success('Archived. Removed from the intake queue.');
    } catch {
      toast.error('Could not archive. Please try again.');
    } finally {
      setArchivingId(null);
    }
  };

  // Conversations that are pending intake (Guest Inbox triage queue)
  const incomingRequests = conversations.filter(c => c.intake_status === 'NEW_REQUEST');

  const filteredConversations = conversations.filter(conv => {
    const matchesSearch = !searchTerm || 
      conv.guestName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      conv.subject?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || conv.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const getChannelIcon = (channel) => {
    switch (channel) {
      case 'EMAIL': return <Mail className="h-4 w-4" />;
      case 'WHATSAPP': return <Phone className="h-4 w-4" />;
      case 'IG': return <Instagram className="h-4 w-4" />;
      default: return <MessageSquare className="h-4 w-4" />;
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'URGENT': return <AlertTriangle className="h-3 w-3" />;
      case 'TIME_SENSITIVE': return <Clock className="h-3 w-3" />;
      case 'INFO_REQUEST': return <HelpCircle className="h-3 w-3" />;
      case 'SALES': return <DollarSign className="h-3 w-3" />;
      default: return <MessageSquare className="h-3 w-3" />;
    }
  };

  // Get the latest inbound message for SLA calculation
  const getLatestInboundMessage = (convId) => {
    // We'd need to load messages for each conversation, but for now just estimate from conversation
    return null;
  };

  const isApprovalMode = selectedProperty?.approvalMode === 'DRAFT_APPROVE';

  const EmptyListState = () => (
    <div className="flex flex-col items-center justify-center h-full py-12 px-4">
      <div className="w-16 h-16 rounded-full bg-stone-100 flex items-center justify-center mb-4">
        <InboxIcon className="h-8 w-8 text-stone-400" />
      </div>
      <h3 className="font-medium text-stone-900 mb-1">Your inbox is empty</h3>
      <p className="text-sm text-stone-500 text-center mb-4">
        New guest messages will land here as they come in.
      </p>
      <div className="flex flex-col gap-2 w-full max-w-[180px]">
        <Button
          variant="outline"
          onClick={() => setShowNewConv(true)}
          className="w-full rounded-full"
          size="sm"
        >
          <Plus className="h-4 w-4 mr-1" />
          New Conversation
        </Button>
      </div>
    </div>
  );

  const EmptyDetailState = () => (
    <div className="flex flex-col items-center justify-center h-full">
      <div className="w-20 h-20 rounded-full bg-stone-100 flex items-center justify-center mb-4">
        <MessageSquare className="h-10 w-10 text-stone-400" />
      </div>
      <h3 className="font-medium text-stone-900 mb-2">No conversation selected</h3>
      <p className="text-sm text-stone-500 text-center max-w-xs mb-6">
        {conversations.length === 0 
          ? "No guest conversations yet. New requests will appear here once your guest desk is active."
          : "Select a conversation from the list to view details."
        }
      </p>
      {conversations.length === 0 && (
        <div className="flex gap-3">
          {isAdmin && (
            <Button 
              onClick={handleSeedData}
              disabled={seeding}
              className="bg-[#3F617E] hover:bg-[#345470] rounded-full"
              data-testid="inbox-load-showcase"
            >
              {seeding ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Database className="h-4 w-4 mr-2" />}
              Load Showcase
            </Button>
          )}
          <Button 
            variant="outline"
            onClick={() => setShowNewConv(true)}
            className="rounded-full"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Conversation
          </Button>
        </div>
      )}
    </div>
  );

  // Maris Assist Panel Component
  const handleSaveEP = async () => {
    if (!linkedTask) return;
    setEpSaving(true);
    try {
      await updateTaskEP(linkedTask.id, epForm);
      toast.success('Experience protection updated');
    } catch { toast.error('Failed to save'); }
    finally { setEpSaving(false); }
  };

  const MarisAssistPanel = () => (
    <div className="hidden xl:flex w-80 border-l border-stone-200 bg-stone-50 flex-col overflow-hidden">
      <div className="p-4 border-b border-stone-200 bg-white">
        <h3 className="font-heading text-lg flex items-center gap-2">
          <PenLine className="h-5 w-5 text-[#3F617E]" />
          Maris Assist
        </h3>
      </div>
      
      <ScrollArea className="flex-1">
        <div className="p-4 space-y-6">
          {/* Experience Protection Section */}
          {linkedTask && (
            <div className="space-y-3" data-testid="ep-section-inbox">
              <h4 className="text-sm font-medium text-stone-700 flex items-center gap-2">
                <Shield className="h-4 w-4 text-[#3F617E]" />
                Experience Protection
              </h4>
              <div className="space-y-2">
                <div>
                  <Label className="text-xs">Signal type</Label>
                  <Select value={epForm.signal_type} onValueChange={(v) => setEpForm({...epForm, signal_type: v})}>
                    <SelectTrigger className="h-8 text-xs" data-testid="ep-signal-select">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="risk">Risk</SelectItem>
                      <SelectItem value="repeat">Repeat</SelectItem>
                      <SelectItem value="opportunity">Opportunity</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Impact area</Label>
                  <Select value={epForm.impact_area} onValueChange={(v) => setEpForm({...epForm, impact_area: v})}>
                    <SelectTrigger className="h-8 text-xs" data-testid="ep-impact-select">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="access">Access</SelectItem>
                      <SelectItem value="comfort">Comfort</SelectItem>
                      <SelectItem value="trust">Trust</SelectItem>
                      <SelectItem value="revenue">Revenue</SelectItem>
                      <SelectItem value="safety">Safety</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Fix route</Label>
                  <Select value={epForm.fix_route} onValueChange={(v) => setEpForm({...epForm, fix_route: v})}>
                    <SelectTrigger className="h-8 text-xs" data-testid="ep-fix-select">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="quick_reply">Quick reply</SelectItem>
                      <SelectItem value="guesthub_update">GuestHub update</SelectItem>
                      <SelectItem value="maintenance">Maintenance</SelectItem>
                      <SelectItem value="policy_decision">Policy decision</SelectItem>
                      <SelectItem value="vendor">Vendor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2 pt-1">
                  <Switch
                    id="owner-decision"
                    checked={epForm.owner_decision_needed}
                    onCheckedChange={(v) => setEpForm({...epForm, owner_decision_needed: v})}
                    className="scale-75"
                  />
                  <Label htmlFor="owner-decision" className="text-xs cursor-pointer">Manager approval needed</Label>
                </div>
                {epForm.owner_decision_needed && (
                  <div>
                    <Input
                      value={epForm.owner_decision_summary}
                      onChange={(e) => setEpForm({...epForm, owner_decision_summary: e.target.value})}
                      placeholder="What do you need approved?"
                      className="h-8 text-xs"
                      data-testid="ep-owner-decision-input"
                    />
                  </div>
                )}
                <Button size="sm" className="w-full bg-[#3F617E] hover:bg-[#345470] rounded-full text-xs h-7" onClick={handleSaveEP} disabled={epSaving} data-testid="ep-save-button">
                  {epSaving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Save className="h-3 w-3 mr-1" />}
                  Save protection
                </Button>
              </div>
            </div>
          )}
          {/* AI Suggested Reply */}
          {aiDraft && (
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-stone-700 flex items-center gap-2">
                <PenLine className="h-4 w-4" />
                Suggested Reply
              </h4>
              <div className="p-3 bg-white rounded-lg border border-stone-200 text-sm text-stone-700">
                {aiDraft}
              </div>
              <div className="flex gap-2">
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="flex-1 rounded-full"
                  onClick={handleRegenerateDraft}
                  disabled={draftLoading}
                >
                  <RefreshCw className={`h-3 w-3 mr-1 ${draftLoading ? 'animate-spin' : ''}`} />
                  Regenerate
                </Button>
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="flex-1 rounded-full"
                  onClick={handleCopyDraft}
                >
                  <Copy className="h-3 w-3 mr-1" />
                  Copy
                </Button>
              </div>
              <Button 
                size="sm" 
                className="w-full bg-[#3F617E] hover:bg-[#345470] rounded-full"
                onClick={handleUseDraft}
              >
                <Check className="h-3 w-3 mr-1" />
                Use this reply
              </Button>
            </div>
          )}

          {/* Quick Replies */}
          {selectedProperty?.quickReplies?.length > 0 && (
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-stone-700 flex items-center gap-2">
                <MessageSquareReply className="h-4 w-4" />
                Quick Replies
              </h4>
              <div className="space-y-2">
                {selectedProperty.quickReplies.map((reply, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleInsertQuickReply(reply)}
                    className="w-full text-left p-2 bg-white rounded-lg border border-stone-200 text-sm text-stone-700 hover:border-[#3F617E] hover:bg-[#3F617E]/5 transition-colors flex items-center justify-between group"
                  >
                    <span className="truncate">{reply}</span>
                    <ChevronRight className="h-4 w-4 text-stone-400 group-hover:text-[#3F617E]" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Property Facts */}
          {selectedProperty && (
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-stone-700 flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                Property Facts
              </h4>
              <div className="space-y-2 text-sm">
                {selectedProperty.faqItems?.slice(0, 3).map((faq, idx) => (
                  <div key={idx} className="p-2 bg-white rounded-lg border border-stone-200">
                    <p className="font-medium text-stone-800">{faq.q}</p>
                    <p className="text-stone-600 text-xs mt-1">{faq.a}</p>
                  </div>
                ))}
                {selectedProperty.policies && (
                  <div className="p-2 bg-white rounded-lg border border-stone-200">
                    <div className="flex items-center gap-2 text-stone-800">
                      <Clock className="h-3 w-3" />
                      <span className="font-medium">Check-in/out</span>
                    </div>
                    <p className="text-stone-600 text-xs mt-1">
                      In: {selectedProperty.policies.checkIn || '3:00 PM'} | Out: {selectedProperty.policies.checkOut || '11:00 AM'}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Upsells */}
          {selectedProperty?.upsells?.length > 0 && (
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-stone-700 flex items-center gap-2">
                <Gift className="h-4 w-4" />
                Upsell Suggestions
              </h4>
              <div className="space-y-2">
                {selectedProperty.upsells.slice(0, 4).map((upsell, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleInsertQuickReply(`We also offer ${upsell.name} for ${upsell.price}. ${upsell.description}`)}
                    className="w-full text-left p-2 bg-white rounded-lg border border-stone-200 hover:border-[#3F617E] hover:bg-[#3F617E]/5 transition-colors"
                  >
                    <div className="flex justify-between items-start">
                      <span className="text-sm font-medium text-stone-800">{upsell.name}</span>
                      <Badge variant="secondary" className="text-xs">{upsell.price}</Badge>
                    </div>
                    <p className="text-xs text-stone-500 mt-1">{upsell.description}</p>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );

  if (loading) {
    return (
      <DeskLayout>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-[calc(100vh-120px)]">
          <Skeleton className="h-full" />
          <Skeleton className="h-full lg:col-span-2" />
        </div>
      </DeskLayout>
    );
  }

  return (
    <DeskLayout>
      <div className="h-[calc(100vh-120px)] animate-fade-in" data-testid="inbox-page">
        <div className="flex flex-col lg:flex-row h-full gap-4 lg:gap-6">
          {/* Conversation List */}
          <div className="w-full lg:w-80 lg:flex-shrink-0 flex flex-col min-h-0">
            <Card className="flex-1 flex flex-col border-stone-200 overflow-hidden">
              <CardHeader className="pb-4 border-b border-stone-100">
                <div className="flex items-center justify-between mb-4">
                  <CardTitle className="font-heading text-xl">Guest Inbox</CardTitle>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1.5">
                      <Switch
                        id="inbox-calm-mode"
                        checked={calmMode}
                        onCheckedChange={setCalmMode}
                        className="scale-75"
                      />
                      <Label htmlFor="inbox-calm-mode" className="text-xs text-stone-500 cursor-pointer">
                        Calm
                      </Label>
                    </div>
                    <Dialog open={showNewConv} onOpenChange={setShowNewConv}>
                      <DialogTrigger asChild>
                        <Button size="sm" className="bg-[#3F617E] hover:bg-[#345470] rounded-full" data-testid="new-conversation-button">
                          <Plus className="h-4 w-4 mr-1" /> New
                        </Button>
                      </DialogTrigger>
                    <DialogContent className="sm:max-w-md">
                      <DialogHeader>
                        <DialogTitle className="font-heading">New Conversation</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <div className="space-y-2">
                          <Label>Property *</Label>
                          <Select value={newConvData.propertyId} onValueChange={(v) => setNewConvData({...newConvData, propertyId: v})}>
                            <SelectTrigger data-testid="new-conv-property-select">
                              <SelectValue placeholder="Select property" />
                            </SelectTrigger>
                            <SelectContent>
                              {properties.map(p => (
                                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Guest Name *</Label>
                          <Input 
                            value={newConvData.guestName} 
                            onChange={(e) => setNewConvData({...newConvData, guestName: e.target.value})}
                            placeholder="Guest name"
                            data-testid="new-conv-guest-name"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Subject *</Label>
                          <Input 
                            value={newConvData.subject} 
                            onChange={(e) => setNewConvData({...newConvData, subject: e.target.value})}
                            placeholder="e.g., Inquiry: Jan 10-14"
                            data-testid="new-conv-subject"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Channel</Label>
                          <Select value={newConvData.channel} onValueChange={(v) => setNewConvData({...newConvData, channel: v})}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="EMAIL">Email</SelectItem>
                              <SelectItem value="WHATSAPP">WhatsApp</SelectItem>
                              <SelectItem value="IG">Instagram</SelectItem>
                              <SelectItem value="OTA">OTA</SelectItem>
                              <SelectItem value="OTHER">Other</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Initial Message (paste inbound)</Label>
                          <Textarea 
                            value={newConvData.initialMessage} 
                            onChange={(e) => setNewConvData({...newConvData, initialMessage: e.target.value})}
                            placeholder="Paste the guest's message here..."
                            rows={4}
                            data-testid="new-conv-message"
                          />
                        </div>
                        <Button 
                          onClick={handleCreateConversation} 
                          className="w-full bg-[#3F617E] hover:bg-[#345470] rounded-full"
                          data-testid="create-conversation-button"
                        >
                          Create Conversation
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                  </div>
                </div>
                
                {/* Search and Filters */}
                <div className="space-y-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-stone-400" />
                    <Input 
                      placeholder="Search..." 
                      className="pl-9"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      data-testid="inbox-search"
                    />
                  </div>
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger data-testid="filter-status">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="NEW">New</SelectItem>
                      <SelectItem value="PENDING">Pending</SelectItem>
                      <SelectItem value="BOOKED">Booked</SelectItem>
                      <SelectItem value="IN_STAY">In-Stay</SelectItem>
                      <SelectItem value="CLOSED">Closed</SelectItem>
                      <SelectItem value="LOST">Lost</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              
              {filteredConversations.length > 0 ? (
                <ScrollArea className="flex-1">
                  <div className="p-2">
                    {/* Incoming Requests — Guest Inbox intake queue */}
                    {incomingRequests.length > 0 && (
                      <div className="mb-3" data-testid="incoming-requests-section">
                        <div className="flex items-center justify-between px-2 py-1.5">
                          <div className="flex items-center gap-1.5">
                            <span className="h-2 w-2 rounded-full bg-amber-500 animate-pulse" />
                            <span className="text-[11px] font-semibold uppercase tracking-wide text-stone-600">
                              Incoming Requests
                            </span>
                          </div>
                          <Badge variant="secondary" className="text-[10px] h-4 px-1.5">
                            {incomingRequests.length}
                          </Badge>
                        </div>
                        {incomingRequests.map((conv) => (
                          <IncomingRequestCard
                            key={conv.id}
                            conv={conv}
                            propertyName={getPropertyName(conv.propertyId)}
                            isSelected={selectedConv?.id === conv.id}
                            onSelect={() => selectConversation(conv)}
                            onAddToRun={() => openAddToRun(conv)}
                            onArchive={() => handleArchiveIntake(conv)}
                            archiving={archivingId === conv.id}
                          />
                        ))}
                        <div className="border-t border-stone-200 my-3" />
                      </div>
                    )}

                    {/* Existing conversation list (excluding pending intake) */}
                    {filteredConversations.filter(c => c.intake_status !== 'NEW_REQUEST').map((conv) => {
                      const { category, why } = categorizeMessage({ bodyText: conv.subject, priority: conv.priority }, conv.subject);
                      const tagStyle = CATEGORY_TAGS[category];
                      const sla = calculateSLATimer(conv.updatedAt);
                      const needsReply = conv.status === 'NEW' || conv.status === 'PENDING' || conv.status === 'IN_STAY';
                      
                      return (
                        <button
                          key={conv.id}
                          onClick={() => selectConversation(conv)}
                          className={`w-full text-left p-3 rounded-xl mb-2 transition-all ${
                            selectedConv?.id === conv.id 
                              ? 'bg-[#3F617E] text-white' 
                              : 'hover:bg-stone-100'
                          }`}
                          data-testid={`conversation-item-${conv.id}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`h-9 w-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                              selectedConv?.id === conv.id ? 'bg-white/20' : 'bg-stone-200'
                            }`}>
                              {getChannelIcon(conv.channel)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium truncate text-sm">{conv.guestName || conv.subject}</p>
                              <p className={`text-xs truncate ${selectedConv?.id === conv.id ? 'text-white/70' : 'text-stone-500'}`}>
                                {conv.subject}
                              </p>
                              {/* Category Tag with "why" and Property Badge */}
                              <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                                {conv.intake_status === 'ADDED_TO_RUN' && (
                                  <Badge
                                    variant="outline"
                                    className={`text-[10px] px-1.5 py-0 h-4 ${
                                      selectedConv?.id === conv.id
                                        ? 'bg-white/20 border-white/30 text-white'
                                        : conv.linked_task_status === 'DONE'
                                          ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                                          : 'bg-[#3F617E]/10 border-[#3F617E]/30 text-[#3F617E]'
                                    }`}
                                  >
                                    {conv.linked_task_status === 'DONE' ? 'Completed' : 'Active task'}
                                  </Badge>
                                )}
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Badge 
                                        variant="outline" 
                                        className={`text-[10px] px-1.5 py-0 h-4 cursor-help ${
                                          selectedConv?.id === conv.id 
                                            ? 'bg-white/20 border-white/30 text-white' 
                                            : tagStyle.color
                                        }`}
                                      >
                                        {getCategoryIcon(category)}
                                        <span className="ml-0.5">{tagStyle.label}</span>
                                      </Badge>
                                    </TooltipTrigger>
                                    <TooltipContent side="right" className="text-xs">
                                      <p>Tagged: {why}</p>
                                      <p className="text-stone-400 mt-1">Maris tags messages to help you prioritize quickly.</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                                
                                {/* SLA Timer */}
                                {needsReply && sla && (
                                  <Badge 
                                    variant={sla.isOverdue ? "destructive" : "secondary"}
                                    className={`text-[10px] px-1.5 py-0 h-4 ${
                                      selectedConv?.id === conv.id 
                                        ? 'bg-white/20 text-white' 
                                        : ''
                                    }`}
                                  >
                                    <Timer className="h-2.5 w-2.5 mr-0.5" />
                                    {sla.text}
                                  </Badge>
                                )}
                                
                                {/* Property Badge */}
                                {conv.propertyId && selectedPropertyId === 'all' && (
                                  <div className={selectedConv?.id === conv.id ? 'opacity-70' : ''}>
                                    <PropertyBadge propertyId={conv.propertyId} variant="inline" size="sm" />
                                  </div>
                                )}
                              </div>
                            </div>
                            <Badge className={`text-[10px] status-${conv.status.toLowerCase()} ${selectedConv?.id === conv.id ? 'bg-white/20 text-white' : ''}`}>
                              {conv.status}
                            </Badge>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </ScrollArea>
              ) : (
                <EmptyListState />
              )}
            </Card>
          </div>

          {/* Conversation Detail + Assist Panel */}
          <div className="flex-1 flex">
            {selectedConv ? (
              <>
                <Card className="flex-1 flex flex-col border-stone-200 overflow-hidden">
                  {/* Intake action bar — only visible while convo is pending triage */}
                  {selectedConv.intake_status === 'NEW_REQUEST' && (
                    <div
                      className="border-b border-amber-200 bg-amber-50 px-4 py-2.5 flex items-center justify-between gap-3"
                      data-testid="intake-action-bar"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="h-2 w-2 rounded-full bg-amber-500 animate-pulse flex-shrink-0" />
                        <span className="text-xs font-medium text-amber-900">
                          New request — not yet on Daily Run.
                        </span>
                        {selectedConv.intake_priority && (
                          <Badge variant="outline" className="text-[10px] h-4 px-1.5 bg-white/60 border-amber-300 text-amber-800">
                            Suggested: {selectedConv.intake_priority === 'HANDLE_NOW' ? 'Handle Now' : 'Due Today'}
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 px-2 text-xs text-stone-600 hover:bg-amber-100"
                          onClick={() => handleArchiveIntake(selectedConv)}
                          disabled={archivingId === selectedConv.id}
                          data-testid="intake-archive-button"
                        >
                          {archivingId === selectedConv.id ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                          ) : (
                            <Archive className="h-3.5 w-3.5 mr-1" />
                          )}
                          Archive
                        </Button>
                        <Button
                          size="sm"
                          className="h-7 px-3 text-xs bg-[#3F617E] hover:bg-[#345470] rounded-full"
                          onClick={() => openAddToRun(selectedConv)}
                          data-testid="intake-add-to-run-button"
                        >
                          <ArrowRight className="h-3.5 w-3.5 mr-1" />
                          Add to Daily Run
                        </Button>
                      </div>
                    </div>
                  )}
                  {/* Active-task status bar — shown after the request has been added to Daily Run */}
                  {selectedConv.intake_status === 'ADDED_TO_RUN' && (
                    <div
                      className={`border-b px-4 py-2.5 flex items-center justify-between gap-3 ${
                        selectedConv.linked_task_status === 'DONE'
                          ? 'border-emerald-200 bg-emerald-50'
                          : 'border-[#3F617E]/20 bg-[#3F617E]/5'
                      }`}
                      data-testid="active-task-status-bar"
                    >
                      <div className="flex items-center gap-2 min-w-0 flex-wrap">
                        {selectedConv.linked_task_status === 'DONE' ? (
                          <>
                            <CheckCircle className="h-3.5 w-3.5 text-emerald-600 flex-shrink-0" />
                            <span className="text-xs font-medium text-emerald-900">Task closed — completed.</span>
                          </>
                        ) : (
                          <>
                            <span className="h-2 w-2 rounded-full bg-[#3F617E] flex-shrink-0" />
                            <span className="text-xs font-medium text-[#345470]">
                              Active task on Daily Run.
                            </span>
                            {selectedConv.linked_task_status && (
                              <Badge variant="outline" className="text-[10px] h-4 px-1.5 bg-white/60 border-[#3F617E]/30 text-[#3F617E]">
                                {selectedConv.linked_task_status === 'NEW' ? 'New' :
                                 selectedConv.linked_task_status === 'IN_PROGRESS' ? 'In progress' :
                                 selectedConv.linked_task_status === 'WAITING' ? 'Waiting' : selectedConv.linked_task_status}
                              </Badge>
                            )}
                          </>
                        )}
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 px-3 text-xs rounded-full border-[#3F617E]/30 text-[#3F617E] hover:bg-[#3F617E]/10"
                        onClick={() => navigate('/desk/daily-run')}
                        data-testid="view-task-button"
                      >
                        <ArrowRight className="h-3.5 w-3.5 mr-1" />
                        View task
                      </Button>
                    </div>
                  )}
                  {/* Header */}
                  <div className="p-4 border-b border-stone-100 bg-white">
                    <div className="flex items-center justify-between">
                      <div>
                        <h2 className="text-lg font-heading font-medium">{selectedConv.guestName || selectedConv.subject}</h2>
                        <div className="text-sm text-stone-500 flex items-center gap-2 flex-wrap">
                          <span>{getPropertyName(selectedConv.propertyId)} • {selectedConv.channel}</span>
                          {isApprovalMode && <Badge variant="outline" className="text-xs">Approval Mode</Badge>}
                        </div>
                        {/* Category Tag with dropdown to change */}
                        {(() => {
                          const { category, why } = categorizeMessage({ bodyText: selectedConv.subject }, selectedConv.subject);
                          const tagStyle = CATEGORY_TAGS[category];
                          const sla = calculateSLATimer(messages[0]?.createdAt);
                          return (
                            <div className="flex items-center gap-2 mt-2">
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Badge variant="outline" className={`text-xs cursor-help ${tagStyle.color}`}>
                                      {getCategoryIcon(category)}
                                      <span className="ml-1">{tagStyle.label}</span>
                                    </Badge>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p className="font-medium">Tagged: {why}</p>
                                    <p className="text-xs text-stone-400 mt-1">Maris tags messages to help you prioritize quickly. You can change the tag anytime.</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                              {sla && (
                                <Badge variant={sla.isOverdue ? "destructive" : "secondary"} className="text-xs">
                                  <Timer className="h-3 w-3 mr-1" />
                                  {sla.text}
                                </Badge>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-full"
                          onClick={handleEscalate}
                        >
                          <Flag className="h-4 w-4 mr-1" />
                          Escalate
                        </Button>
                        <Select value={selectedConv.status} onValueChange={handleStatusChange}>
                          <SelectTrigger className="w-28" data-testid="conversation-status-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="NEW">New</SelectItem>
                            <SelectItem value="PENDING">Pending</SelectItem>
                            <SelectItem value="BOOKED">Booked</SelectItem>
                            <SelectItem value="IN_STAY">In-Stay</SelectItem>
                            <SelectItem value="CLOSED">Closed</SelectItem>
                            <SelectItem value="LOST">Lost</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  {/* Messages */}
                  <ScrollArea className="flex-1 p-4">
                    <div className="space-y-4">
                      {messages.map((msg) => {
                        const { category, why } = msg.direction === 'INBOUND' 
                          ? categorizeMessage(msg, selectedConv?.subject) 
                          : { category: null, why: null };
                        const tagStyle = category ? CATEGORY_TAGS[category] : null;
                        const sla = msg.direction === 'INBOUND' && msg.needsReply ? calculateSLATimer(msg.createdAt) : null;
                        
                        return (
                          <div 
                            key={msg.id}
                            className={`flex ${msg.direction === 'OUTBOUND' ? 'justify-end' : 'justify-start'}`}
                          >
                            <div className={`max-w-[75%] p-3 rounded-2xl ${
                              msg.direction === 'OUTBOUND' 
                                ? 'bg-[#3F617E] text-white rounded-br-md' 
                                : 'bg-stone-100 text-stone-900 rounded-bl-md'
                            }`}>
                              <p className="whitespace-pre-wrap text-sm">{msg.bodyText}</p>
                              <div className={`flex items-center gap-2 mt-2 flex-wrap ${msg.direction === 'OUTBOUND' ? 'text-white/60' : 'text-stone-400'}`}>
                                <span className="text-xs">{format(new Date(msg.createdAt), 'MMM d, h:mm a')}</span>
                                {msg.direction === 'INBOUND' && tagStyle && (
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Badge variant="outline" className={`text-[10px] px-1 py-0 h-4 cursor-help ${tagStyle.color}`}>
                                          {getCategoryIcon(category)}
                                          <span className="ml-0.5">{tagStyle.label}</span>
                                        </Badge>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p>Tagged: {why}</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                )}
                                {sla && (
                                  <Badge variant={sla.isOverdue ? "destructive" : "secondary"} className="text-[10px] h-4">
                                    <Timer className="h-2.5 w-2.5 mr-0.5" />
                                    {sla.text}
                                  </Badge>
                                )}
                                {msg.direction === 'INBOUND' && msg.needsReply && (
                                  <Badge variant="destructive" className="text-[10px] h-4">Needs Reply</Badge>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </ScrollArea>

                  {/* Reply Composer — hidden while the conversation is still pending intake.
                       Guest Inbox is for intake review only; the composer reappears after the
                       request has been added to the Daily Run. */}
                  {selectedConv.intake_status === 'NEW_REQUEST' ? (
                    <div
                      className="p-5 border-t border-stone-100 bg-stone-50"
                      data-testid="intake-decision-panel"
                    >
                      <div className="rounded-xl border border-stone-200 bg-white p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-stone-900">
                            Decide what happens with this request.
                          </p>
                          <p className="text-xs text-stone-500 mt-0.5 leading-relaxed">
                            This request is not on the Daily Run yet. Add it to the run so the team can assign, handle, and track it.
                          </p>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 px-3 text-xs text-stone-600 hover:bg-stone-100 rounded-full"
                            onClick={() => handleArchiveIntake(selectedConv)}
                            disabled={archivingId === selectedConv.id}
                            data-testid="intake-archive-button-panel"
                          >
                            {archivingId === selectedConv.id ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                            ) : (
                              <Archive className="h-3.5 w-3.5 mr-1" />
                            )}
                            Archive
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 px-3 text-xs rounded-full"
                            onClick={handleEscalate}
                            data-testid="intake-escalate-button"
                          >
                            <Flag className="h-3.5 w-3.5 mr-1" />
                            Escalate
                          </Button>
                          <Button
                            size="sm"
                            className="h-8 px-3.5 text-xs bg-[#3F617E] hover:bg-[#345470] rounded-full"
                            onClick={() => openAddToRun(selectedConv)}
                            data-testid="intake-add-to-run-button-panel"
                          >
                            <ArrowRight className="h-3.5 w-3.5 mr-1" />
                            Add to Daily Run
                          </Button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="p-4 border-t border-stone-100 bg-stone-50 space-y-3">
                      {/* Tone Toggles */}
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-stone-500">Tone:</span>
                        <div className="flex gap-1">
                          {['WARM', 'REFINED', 'SHORT'].map((tone) => (
                            <Button
                              key={tone}
                              size="sm"
                              variant={selectedTone === tone ? 'default' : 'outline'}
                              className={`h-7 text-xs rounded-full ${selectedTone === tone ? 'bg-[#3F617E]' : ''}`}
                              onClick={() => setSelectedTone(tone)}
                            >
                              {tone.charAt(0) + tone.slice(1).toLowerCase()}
                            </Button>
                          ))}
                        </div>
                        <div className="flex-1" />
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={handleGenerateDraft}
                          disabled={draftLoading}
                          className="rounded-full"
                          data-testid="generate-draft-button"
                        >
                          {draftLoading ? (
                            <Loader2 className="h-4 w-4 animate-spin mr-1" />
                          ) : (
                            <PenLine className="h-4 w-4 mr-1" />
                          )}
                          Draft reply
                        </Button>
                      </div>

                      {/* Textarea */}
                      <Textarea
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Write a reply..."
                        className="min-h-[100px] resize-none"
                        data-testid="message-input"
                      />

                      {/* Action Buttons */}
                      <div className="flex items-center gap-2">
                        <Button 
                          variant="outline"
                          onClick={handleSaveDraft}
                          className="rounded-full"
                          disabled={!newMessage.trim()}
                        >
                          <Save className="h-4 w-4 mr-1" />
                          Save Draft
                        </Button>
                        <div className="flex-1" />
                        {isApprovalMode ? (
                          <Button 
                            onClick={() => handleSendMessage(true)}
                            disabled={!newMessage.trim() || sending}
                            className="bg-amber-600 hover:bg-amber-700 rounded-full"
                            data-testid="send-for-approval-button"
                          >
                            {sending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <SendHorizonal className="h-4 w-4 mr-1" />}
                            Send for Approval
                          </Button>
                        ) : (
                          <Button 
                            onClick={() => handleSendMessage(false)}
                            disabled={!newMessage.trim() || sending}
                            className="bg-[#3F617E] hover:bg-[#345470] rounded-full"
                            data-testid="send-message-button"
                          >
                            {sending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}
                            Send
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                </Card>

                {/* Maris Assist Panel */}
                {showAssistPanel && <MarisAssistPanel />}
              </>
            ) : (
              <Card className="flex-1 flex items-center justify-center border-stone-200">
                <EmptyDetailState />
              </Card>
            )}
          </div>
        </div>
        
        {/* Escalate Modal */}
        <Dialog open={showEscalateModal} onOpenChange={setShowEscalateModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading">Escalate this issue</DialogTitle>
              <DialogDescription>
                Create a clear task and send a calm update to the guest.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Reason</Label>
                <Select value={escalateData.reason} onValueChange={(v) => setEscalateData({...escalateData, reason: v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select reason" />
                  </SelectTrigger>
                  <SelectContent>
                    {ESCALATION_REASONS.map(r => (
                      <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Assign to</Label>
                <Select value={escalateData.assignTo} onValueChange={(v) => setEscalateData({...escalateData, assignTo: v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select assignee" />
                  </SelectTrigger>
                  <SelectContent>
                    {ESCALATION_ASSIGN_OPTIONS.map(o => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Internal notes</Label>
                <Textarea 
                  value={escalateData.internalNotes} 
                  onChange={(e) => setEscalateData({...escalateData, internalNotes: e.target.value})}
                  placeholder="Add any context for the team..."
                  rows={3}
                />
              </div>
              <div className="pt-2 space-y-3">
                <div className="text-xs text-stone-500">
                  Guest update will be added to composer:
                </div>
                <div className="p-3 bg-stone-50 rounded-lg text-sm text-stone-600 italic">
                  &ldquo;{GUEST_UPDATE_SCRIPTS.default}&rdquo;
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <Button 
                  variant="outline"
                  onClick={() => setShowEscalateModal(false)} 
                  className="flex-1 rounded-full"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleCreateEscalation} 
                  className="flex-1 bg-[#3F617E] hover:bg-[#345470] rounded-full"
                >
                  Create task
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Add-to-Daily-Run confirm modal */}
        <AddToRunModal
          open={addToRunOpen}
          onOpenChange={setAddToRunOpen}
          conversation={addToRunConv}
          onConfirm={handleConfirmAddToRun}
        />
      </div>
    </DeskLayout>
  );
};

export default Inbox;
